import React, { useState } from 'react';

function Section({ title, icon, children }) {
  return (
    <div className="card mb-12">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontFamily: 'var(--head)', fontSize: 15, fontWeight: 700 }}>{title}</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>
      {children}
    </div>
  );
}

function FG({ label, hint, children }) {
  return (
    <div className="form-group">
      <div className="form-label"><span className="form-label-text">{label}</span></div>
      {children}
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );
}

export default function Settings({ settings, saveSettings, addToast, apiStatus, fetchKAPSData, fetchKICPData, liveRate }) {
  const [form, setForm] = useState(settings);
  const [testing, setTesting] = useState({ kaps: false, kicp: false });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = () => saveSettings(form);

  const testKAPS = async () => {
    if (!form.kaps_api_url) { addToast('Enter KAPS API URL first', 'error'); return; }
    setTesting(t => ({ ...t, kaps: true }));
    try {
      const res = await fetch(`${form.kaps_api_url}/api/pp/stock-summary`, {
        headers: { 'Authorization': `Bearer ${form.kaps_api_key}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        addToast('KAPS connection successful!', 'success');
      } else {
        addToast(`KAPS responded with HTTP ${res.status}`, 'error');
      }
    } catch (e) {
      addToast(`KAPS test failed: ${e.message}`, 'error');
    } finally {
      setTesting(t => ({ ...t, kaps: false }));
    }
  };

  const testKICP = async () => {
    if (!form.kicp_api_url) { addToast('Enter KICP API URL first', 'error'); return; }
    setTesting(t => ({ ...t, kicp: true }));
    try {
      const res = await fetch(`${form.kicp_api_url}/api/film/orders-summary?film_type=all`, {
        headers: { 'Authorization': `Bearer ${form.kicp_api_token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        addToast('KICP connection successful!', 'success');
      } else {
        addToast(`KICP responded with HTTP ${res.status}`, 'error');
      }
    } catch (e) {
      addToast(`KICP test failed: ${e.message}`, 'error');
    } finally {
      setTesting(t => ({ ...t, kicp: false }));
    }
  };

  return (
    <div>
      <div className="page-hdr">
        <h2>Settings</h2>
        <p>Configure API connections to your internal systems (KAPS MRP, KICP) for real-time data refresh. Settings are saved locally on this device.</p>
      </div>

      {/* General */}
      <Section title="General" icon="🏢">
        <div className="g2">
          <FG label="Company Name">
            <input type="text" className="form-input" value={form.company_name || ''} onChange={e => set('company_name', e.target.value)} />
          </FG>
          <div>
            <div className="form-label"><span className="form-label-text">USD/CNY Exchange Rate</span></div>
            <div className="alert alert-blu" style={{ marginTop: 4 }}>
              <span className="alert-icon">⚡</span>
              <div className="alert-body">
                <div>Live USD/CNY: <strong>{liveRate ? Number(liveRate).toFixed(4) : '—'}</strong> — auto-fetched every 15 min</div>
                <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>实时美元/人民币汇率：<strong>{liveRate ? Number(liveRate).toFixed(4) : '—'}</strong>——每 15 分钟自动更新</div>
              </div>
            </div>
          </div>
          <FG label="Default Conversion Cost (¥/MT)" hint="Pending finance team confirmation">
            <input type="number" className="form-input" step="50" value={form.default_conversion_cost || 1800} onChange={e => set('default_conversion_cost', parseFloat(e.target.value))} />
          </FG>
        </div>
      </Section>

      {/* Alert Thresholds */}
      <Section title="Alert Thresholds" icon="🔔">
        <div className="g3">
          <FG label="STI Alert Threshold" hint="Show red alert when STI exceeds this level">
            <input type="number" className="form-input" min="0" max="100" value={form.sti_alert_threshold || 75} onChange={e => set('sti_alert_threshold', parseFloat(e.target.value))} />
          </FG>
          <FG label="MSI Alert Threshold" hint="Show alert when MSI exceeds this level">
            <input type="number" className="form-input" min="0" max="100" value={form.msi_alert_threshold || 70} onChange={e => set('msi_alert_threshold', parseFloat(e.target.value))} />
          </FG>
          <FG label="Safety Breach Alert (days)" hint="Show urgent alert when safety breach is within this many days">
            <input type="number" className="form-input" min="1" max="60" value={form.safety_breach_alert_days || 10} onChange={e => set('safety_breach_alert_days', parseInt(e.target.value))} />
          </FG>
        </div>
      </Section>

      {/* KAPS API */}
      <Section title="KAPS MRP API — Internal Inventory System" icon="🏭">
        <div className="alert alert-blu mb-12">
          <span className="alert-icon">ℹ️</span>
          <div className="alert-body">
            <strong>KAPS MRP is at 172.16.10.28</strong> — only accessible from within Kunlene's company network. If you are accessing this app from outside the company network (e.g. from home), KAPS API calls will fail. This is expected.
          </div>
        </div>
        <div className="alert alert-amb mb-12">
          <span className="alert-icon">🔧</span>
          <div className="alert-body">
            <div>API integration in progress — IT team is implementing server endpoints. Use direct system access until Settings API is confirmed working: KAPS at 172.16.10.28, KICP at m.kunlene-sz.com:8218.</div>
            <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>API 集成进行中 — IT 团队正在实施服务器端点。在设置页面 API 确认可用之前，请使用直接系统访问：KAPS 地址 172.16.10.28，KICP 地址 m.kunlene-sz.com:8218。</div>
          </div>
        </div>
        <div className="form-group">
          <div className="form-label"><span className="form-label-text">Enable KAPS API</span></div>
          <div className="radio-group" style={{ maxWidth: 200 }}>
            <div className="radio-opt">
              <input type="radio" id="kaps-on" name="kaps_en" checked={form.kaps_enabled} onChange={() => set('kaps_enabled', true)} />
              <label htmlFor="kaps-on">Enabled</label>
            </div>
            <div className="radio-opt">
              <input type="radio" id="kaps-off" name="kaps_en" checked={!form.kaps_enabled} onChange={() => set('kaps_enabled', false)} />
              <label htmlFor="kaps-off">Disabled</label>
            </div>
          </div>
        </div>
        <div className="g2">
          <FG label="KAPS API Base URL" hint="e.g. http://172.16.10.28:8080 — include port if needed">
            <input type="text" className="form-input" placeholder="http://172.16.10.28:8080" value={form.kaps_api_url || ''} onChange={e => set('kaps_api_url', e.target.value)} />
          </FG>
          <FG label="KAPS API Key / Bearer Token" hint="From your IT team — keep private, stored locally only">
            <input type="password" className="form-input" placeholder="••••••••••••" value={form.kaps_api_key || ''} onChange={e => set('kaps_api_key', e.target.value)} />
          </FG>
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="label-mono mb-8">API Endpoint called by this app</div>
          {[
            { path: '/api/pp/stock-summary', desc: 'Stock, transit, PR, avg cost, safety stock, daily demand' },
          ].map(ep => (
            <div key={ep.path} style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
              <span style={{ fontFamily: 'var(--mono)', color: 'var(--blu)', minWidth: 200 }}>{ep.path}</span>
              <span style={{ color: 'var(--text2)' }}>{ep.desc}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>Only this endpoint is currently called by the app.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn btn-secondary" onClick={testKAPS} disabled={testing.kaps}>
            {testing.kaps ? 'Testing...' : '🔌 Test KAPS Connection'}
          </button>
          <button className="btn btn-success btn-sm" onClick={fetchKAPSData}>
            ⬇️ Fetch KAPS Now
          </button>
          <span className={`badge badge-${apiStatus.kaps === 'ok' ? 'grn' : apiStatus.kaps === 'error' ? 'red' : 'neutral'}`}>
            {apiStatus.kaps === 'ok' ? '✓ Connected' : apiStatus.kaps === 'error' ? '✕ Error' : 'Unchecked'}
          </span>
        </div>
      </Section>

      {/* KICP API */}
      <Section title="KICP — Internal Commercial Platform" icon="📦">
        <div className="alert alert-blu mb-12">
          <span className="alert-icon">ℹ️</span>
          <div className="alert-body">
            <strong>KICP is at m.kunlene-sz.com:8218</strong> — a Uni-app mobile platform. Access may be restricted to China IP addresses only. The API token should be extracted using the Chrome DevTools method sent to your IT team.
          </div>
        </div>
        <div className="alert alert-amb mb-12">
          <span className="alert-icon">🔧</span>
          <div className="alert-body">
            <div>API integration in progress — IT team is implementing server endpoints. Use direct system access until Settings API is confirmed working: KAPS at 172.16.10.28, KICP at m.kunlene-sz.com:8218.</div>
            <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>API 集成进行中 — IT 团队正在实施服务器端点。在设置页面 API 确认可用之前，请使用直接系统访问：KAPS 地址 172.16.10.28，KICP 地址 m.kunlene-sz.com:8218。</div>
          </div>
        </div>
        <div className="form-group">
          <div className="form-label"><span className="form-label-text">Enable KICP API</span></div>
          <div className="radio-group" style={{ maxWidth: 200 }}>
            <div className="radio-opt">
              <input type="radio" id="kicp-on" name="kicp_en" checked={form.kicp_enabled} onChange={() => set('kicp_enabled', true)} />
              <label htmlFor="kicp-on">Enabled</label>
            </div>
            <div className="radio-opt">
              <input type="radio" id="kicp-off" name="kicp_en" checked={!form.kicp_enabled} onChange={() => set('kicp_enabled', false)} />
              <label htmlFor="kicp-off">Disabled</label>
            </div>
          </div>
        </div>
        <div className="g2">
          <FG label="KICP API Base URL" hint="e.g. http://m.kunlene-sz.com:8218">
            <input type="text" className="form-input" placeholder="http://m.kunlene-sz.com:8218" value={form.kicp_api_url || ''} onChange={e => set('kicp_api_url', e.target.value)} />
          </FG>
          <FG label="KICP Bearer Token" hint="Extracted via Chrome DevTools — expires periodically, refresh as needed">
            <input type="password" className="form-input" placeholder="••••••••••••" value={form.kicp_api_token || ''} onChange={e => set('kicp_api_token', e.target.value)} />
          </FG>
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="label-mono mb-8">API Endpoints called by this app</div>
          {[
            { path: '/api/film/orders-summary?film_type=GLOSS_18UM', desc: '已下订单 — Gloss 18µm qty + avg sell price, filtered by 发货日期 min' },
            { path: '/api/film/orders-summary?film_type=all', desc: 'Portfolio weighted avg, all film types, breakdown array' },
          ].map(ep => (
            <div key={ep.path} style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 11 }}>
              <span style={{ fontFamily: 'var(--mono)', color: 'var(--blu)', minWidth: 240, wordBreak: 'break-all' }}>{ep.path}</span>
              <span style={{ color: 'var(--text2)' }}>{ep.desc}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>Only these endpoints are currently called by the app.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn btn-secondary" onClick={testKICP} disabled={testing.kicp}>
            {testing.kicp ? 'Testing...' : '🔌 Test KICP Connection'}
          </button>
          <button className="btn btn-success btn-sm" onClick={fetchKICPData}>
            ⬇️ Fetch KICP Now
          </button>
          <span className={`badge badge-${apiStatus.kicp === 'ok' ? 'grn' : apiStatus.kicp === 'error' ? 'red' : 'neutral'}`}>
            {apiStatus.kicp === 'ok' ? '✓ Connected' : apiStatus.kicp === 'error' ? '✕ Error' : 'Unchecked'}
          </span>
        </div>
      </Section>

      {/* Supabase note */}
      <Section title="Database Connection (Supabase)" icon="🗄️">
        <div className="alert alert-grn mb-12">
          <span className="alert-icon">✓</span>
          <div className="alert-body">
            Supabase credentials are baked into the build via environment variables (<code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code>). Supabase is accessible from mainland China — no VPN needed. To change the Supabase project, update .env.local and rebuild.
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.75 }}>
          <strong>Tables used:</strong> <code>pp_map_runs</code> (model runs + learning loop) · <code>predictions</code> (VERITAS cron snapshots) · <code>actuals</code> (DCE daily closes) · <code>daily_market_signals</code> (AI news classifier) · <code>user_profiles</code> (auth)<br />
          <strong>App settings</strong> (API URLs, thresholds, company name) are stored in <strong>localStorage</strong>, not Supabase — they do not sync across devices.<br />
          <strong>Run the schema:</strong> Copy <code>supabase-schema.sql</code> from the project root and run in Supabase SQL Editor to create all tables.
        </div>
      </Section>

      {/* Save */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button className="btn btn-primary btn-lg" onClick={handleSave}>
          💾 Save All Settings
        </button>
        <button className="btn btn-secondary" onClick={() => setForm(settings)}>
          ↩ Reset to Saved
        </button>
      </div>
    </div>
  );
}
